import { Box, Typography, Paper, IconButton } from "@mui/material";
import { OpenInNewRounded, ArrowRightRounded } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { EvaluationResponse } from "../../Models/evaluation";
import { useTerminology } from "../../Core/useTerminology";
import colors from "../../Constants/colors";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { EvaluationStatus } from "../../Enums/companyStatus";

export interface InProgressEvaluationsCardProps {
  evaluations: EvaluationResponse[];
  onOpenEvaluation?: (evaluationId: number) => void;
}

export function InProgressEvaluationsCard({
  evaluations,
  onOpenEvaluation,
}: InProgressEvaluationsCardProps) {
  const { t } = useTranslation("singleCompanyComponent");
  const terminology = useTerminology();
  const { colors: workspace } = useWorkspaceColors();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 2,
        animation: "pulse-border-orange 2s ease-in-out infinite",
        "@keyframes pulse-border-orange": {
          "0%": {
            boxShadow: `0 0 0 0 ${workspace.accent}40`,
          },
          "100%": {
            boxShadow: `0 0 0 8px rgba(253, 152, 0, 0)`,
          },
        },
      }}
    >
      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: workspace.primary_dark,
          mb: 2,
          fontSize: "1.1rem",
        }}
      >
        {t("inProgressEvaluations.title")}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {evaluations.map((evaluation) => (
          <Box
            key={evaluation.evaluation_id}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1,
              border: `1px solid ${colors.gray200}`,
              borderRadius: 2,
              "&:hover": { bgcolor: `${workspace.primary_dark}0F` },
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, color: colors.gray900 }}
                >
                  {evaluation.question_list_name}
                </Typography>
                <ArrowRightRounded
                  sx={{ color: colors.gray400, fontSize: "1rem" }}
                />
                <Typography variant="caption" sx={{ color: colors.gray600 }}>
                  {Number(evaluation.status) === EvaluationStatus.PAUSED
                    ? t("inProgressEvaluations.pausedEvaluation")
                    : t("inProgressEvaluations.gradingEntity", {
                        entity: terminology.singular,
                      })}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, ml: 2 }}>
              <IconButton
                size="small"
                onClick={() => onOpenEvaluation?.(evaluation.evaluation_id)}
                sx={{ "&:hover": { bgcolor: colors.orange50 } }}
              >
                <OpenInNewRounded sx={{ color: workspace.accent }} />
              </IconButton>
            </Box>
          </Box>
        ))}
      </Box>
    </Paper>
  );
}
